import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Rocket, Lightbulb, ShoppingBag, CalendarDays,
  Camera, Store, Tag, GraduationCap, Compass, Sparkles
} from 'lucide-react';
import ServiceCTA from '../../components/ServiceCTA';
import TrustBadge from '../../components/TrustBadge';

const TikTokLiveService = () => {
  // 提供サービスの例（今後、他のライブ配信プラットフォームや支援メニューも追加しやすい構成）
  const serviceExamples = [
    { icon: <Rocket className="w-6 h-6" />, title: 'TikTok LIVE導入サポート', description: 'アカウント準備から初回配信まで、はじめての方にもわかりやすくご案内します。' },
    { icon: <Lightbulb className="w-6 h-6" />, title: 'ライブ配信企画', description: '目的に合わせた配信の企画・構成づくりを一緒に考えます。' },
    { icon: <ShoppingBag className="w-6 h-6" />, title: 'ライブコマース支援', description: 'ライブ配信を活用した商品販売・オンライン販路拡大をサポートします。' },
    { icon: <CalendarDays className="w-6 h-6" />, title: '地域イベント配信', description: '地域のお祭りやイベントの様子を、全国・世界へお届けします。' },
    { icon: <Camera className="w-6 h-6" />, title: '観光ライブ配信', description: '地域の観光スポットや魅力を発信し、来訪のきっかけづくりを支援します。' },
    { icon: <Store className="w-6 h-6" />, title: '店舗ライブ配信', description: 'お店の雰囲気やこだわりを、ライブでリアルに伝えるお手伝いをします。' },
    { icon: <Tag className="w-6 h-6" />, title: '商品PRライブ', description: '地域産品や商品の魅力を、ライブ配信で分かりやすくPRします。' },
    { icon: <GraduationCap className="w-6 h-6" />, title: 'クリエイター育成', description: 'ライブ配信の始め方から継続的な成長まで、一人ひとりに寄り添って支援します。' },
    { icon: <Compass className="w-6 h-6" />, title: 'TikTok LIVE運営アドバイス', description: '配信の振り返りや改善点のご提案など、運営面をサポートします。' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="py-16" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center fade-in">
            <TrustBadge className="mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
              TikTok LIVE・ライブ配信サポート
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              TikTok LIVEを活用したライブ配信の企画・準備・配信サポートを行います。
              集客、商品PR、店舗紹介、地域プロモーション、観光PRなど、目的に合わせたライブ配信をご提案します。
            </p>
          </div>
        </div>
      </section>

      {/* Intro / Agency note */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-8 md:p-10 shadow-sm">
              <h2 className="text-2xl font-bold mb-5" style={{ color: '#2C2C2C' }}>
                TikTok LIVE公認エージェンシーとして
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                さくらグローバル合同会社は、TikTok LIVE公認エージェンシーとして認定されました。
                SLBSでは、この認定を活かし、地域の事業者様やクリエイターの皆様に向けて、TikTok LIVEを活用した
                ライブ配信による集客・認知拡大・販路拡大をサポートしています。
              </p>
              <p className="text-gray-700 leading-relaxed">
                店舗・観光・地域産品・サービスなど、それぞれの魅力を全国、そして世界へ届けるための
                情報発信を、一歩ずつお手伝いします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Examples - Feature Cards */}
      <section className="py-16" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
              サービス内容の例
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              目的やご状況に合わせて、必要なサポートを組み合わせてご提案します。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {serviceExamples.map((item, index) => (
              <div
                key={index}
                className="bg-white p-7 rounded-2xl border border-gray-100 shadow-sm hover-lift"
                data-testid={`tiktok-service-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#FFF7FA', color: '#C77A93' }}>
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#2C2C2C' }}>
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Creator Support */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100">
              <div className="flex items-center mb-5">
                <Sparkles className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: '#C77A93' }} />
                <h2 className="text-2xl font-bold" style={{ color: '#2C2C2C' }}>
                  クリエイター支援
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                TikTok LIVE公認エージェンシーとして、クリエイターの皆様の活動をサポートしています。
                ライブ配信の始め方から配信改善、コミュニティづくり、継続的な成長まで、
                一人ひとりに寄り添いながら支援します。
              </p>
              <ul className="space-y-3 mt-6">
                {[
                  'ライブ配信の始め方・機材準備のご相談',
                  '配信内容の振り返りと改善のご提案',
                  'ファンコミュニティづくりのサポート',
                  '継続的な成長に向けた伴走支援',
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 mt-1 mr-3 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Future scalability note */}
      <section className="pb-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: '#FFF7FA' }}>
              <p className="text-gray-700 leading-relaxed">
                現在はTikTok LIVEを中心にサポートを行っていますが、
                今後は他のライブ配信サービスやクリエイター向けプログラムの拡充も予定しています。
                まずはお気軽にご相談ください。
              </p>
              <Link to="/pricing" className="inline-block text-sm text-[#C77A93] hover:text-[#2C2C2C] font-medium mt-4">
                料金の目安を見る →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ServiceCTA
        title="ライブ配信で、地域の魅力を全国へ。"
        description="TikTok LIVEを活用した情報発信について、お気軽にご相談ください。"
        buttonText="無料相談はこちら"
      />
    </div>
  );
};

export default TikTokLiveService;
